import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, EventAvailable } from '@mui/icons-material';

export type ProjectSessionStatus = 'upcoming' | 'active' | 'completed';

export interface CreateProjectSessionPayload {
  session_name: string;
  session_start_date: string;
  session_end_date?: string | null;
  status: ProjectSessionStatus;
}

interface CreateProjectSessionDialogProps {
  open: boolean;
  projectName: string;
  isSubmitting: boolean;
  onClose: () => void;
  onCreate: (payload: CreateProjectSessionPayload) => Promise<void>;
}

const initialForm: CreateProjectSessionPayload = {
  session_name: '',
  session_start_date: '',
  session_end_date: '',
  status: 'upcoming',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255,255,255,0.04)',
    color: '#fff',
    borderRadius: '8px',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
    '&:hover fieldset': { borderColor: 'rgba(16,185,129,0.45)' },
    '&.Mui-focused fieldset': { borderColor: '#10B981' },
    '& input::-webkit-calendar-picker-indicator': { filter: 'invert(1)' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.45)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#10B981' },
  '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.5)' },
};

const CreateProjectSessionDialog: FC<CreateProjectSessionDialogProps> = ({
  open,
  projectName,
  isSubmitting,
  onClose,
  onCreate,
}) => {
  const [formData, setFormData] = useState<CreateProjectSessionPayload>(initialForm);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setFormData(initialForm);
      setError('');
    }
  }, [open]);

  const handleChange = (field: keyof CreateProjectSessionPayload, value: string | ProjectSessionStatus) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const handleSubmit = async () => {
    const sessionName = formData.session_name.trim();
    if (!sessionName || !formData.session_start_date) {
      setError('Session name and start date are required.');
      return;
    }

    if (
      formData.session_end_date &&
      new Date(formData.session_end_date) < new Date(formData.session_start_date)
    ) {
      setError('End date must be equal to or later than the start date.');
      return;
    }

    await onCreate({
      session_name: sessionName,
      session_start_date: formData.session_start_date,
      session_end_date: formData.session_end_date || null,
      status: formData.status,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: 'rgba(8, 10, 14, 0.98)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '16px',
          backdropFilter: 'blur(24px)',
          color: '#fff',
          fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          p: 3,
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              background: 'rgba(16,185,129,0.12)',
              border: '1px solid rgba(16,185,129,0.24)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#10B981',
              flexShrink: 0,
            }}
          >
            <EventAvailable />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '18px', fontWeight: 700, lineHeight: 1.2 }}>
              Create Project Session
            </Typography>
            <Typography sx={{ mt: 0.75, fontSize: '12px', color: 'rgba(255,255,255,0.55)' }}>
              {projectName}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={isSubmitting}
          sx={{ color: 'rgba(255, 255, 255, 0.4)', '&:hover': { color: '#fff' } }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Session Name"
          value={formData.session_name}
          onChange={(event) => handleChange('session_name', event.target.value)}
          fullWidth
          required
          disabled={isSubmitting}
          sx={fieldSx}
        />

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField
            label="Start Date"
            type="date"
            value={formData.session_start_date}
            onChange={(event) => handleChange('session_start_date', event.target.value)}
            fullWidth
            required
            disabled={isSubmitting}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={fieldSx}
          />
          <TextField
            label="End Date"
            type="date"
            value={formData.session_end_date || ''}
            onChange={(event) => handleChange('session_end_date', event.target.value)}
            fullWidth
            disabled={isSubmitting}
            inputProps={{ min: formData.session_start_date || undefined }}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={fieldSx}
          />
        </Box>

        <TextField
          label="Status"
          value={formData.status}
          onChange={(event) => handleChange('status', event.target.value as ProjectSessionStatus)}
          select
          fullWidth
          disabled={isSubmitting}
          sx={fieldSx}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  backgroundColor: '#101216',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.1)',
                },
              },
            },
          }}
        >
          <MenuItem value="upcoming">Upcoming</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="completed">Completed</MenuItem>
        </TextField>

        {error && (
          <Typography sx={{ fontSize: '12px', color: '#f87171', fontWeight: 600 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <Box
        sx={{
          p: 2.5,
          borderTop: '1px solid rgba(255, 255, 255, 0.06)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.5,
        }}
      >
        <Button
          size="small"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{ color: 'rgba(255,255,255,0.62)', fontWeight: 700 }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          sx={{
            bgcolor: '#10B981',
            color: '#07110d',
            fontWeight: 800,
            borderRadius: '6px',
            minWidth: 140,
            '&:hover': { bgcolor: '#059669' },
          }}
        >
          {isSubmitting ? <CircularProgress size={18} sx={{ color: '#07110d' }} /> : 'Create Session'}
        </Button>
      </Box>
    </Dialog>
  );
};

export default CreateProjectSessionDialog;
